import type {
  LabelMap,
  SeverityMap,
  StatusSeverity
} from '@/components/atoms/badges/twn-status-indicator/TwnStatusIndicator.types'

/**
 * What one inventory item — one variant at one location — says about its stock.
 *
 * The line is the merchant's own re-order point, not a number this app invented.
 * Backend: `Domain\Inventory\QueryBuilders\InventoryItemQueryBuilder::belowReorderPoint()`.
 *
 * @remarks
 * Values match that query builder's vocabulary so the filter chip and the badge can
 * speak one language, but they are not a backend enum — no endpoint returns them today.
 */
const STOCK_LEVEL = {
  /**
   * The merchant never set a re-order point here, so there is no line to be under.
   *
   * @remarks
   * Not "unknown". A null point is a deliberate absence: replenishment excludes the item
   * on purpose, and the report has no dismissal table because clearing the point *is* the
   * dismissal.
   *
   * It also outranks nothing. A location the merchant never claimed cannot report out of
   * stock however empty it is, or a tenant with two dozen locations reads red on everything.
   */
  NO_REORDER_POINT: 'no_reorder_point',
  /** Nothing on the shelf, at a location the merchant manages. */
  OUT_OF_STOCK: 'out_of_stock',
  /** At or below the point the merchant set. `<=`, matching the backend — resting on the line is asking for an order. */
  BELOW_REORDER_POINT: 'below_reorder_point',
  /** Above the point. */
  IN_STOCK: 'in_stock',
  /**
   * The variant tracks stock, and no location holds an inventory record for it.
   *
   * @remarks
   * Not the same as out of stock: there is no shelf, rather than an empty one. The domain
   * already reads a missing row this way — `ListSourceLocationsAction` calls it "has never
   * stocked the product". It is usually a gap rather than a decision, because creating a
   * location creates no inventory rows.
   */
  NOT_STOCKED: 'not_stocked',
  /**
   * The merchant switched inventory tracking off, so a sale moves no stock.
   *
   * @remarks
   * A real setting rather than an absence — `BaseAdjustInventoryQuantityAction` reads the
   * same flag — and usually paired with "Allow out of stock", which is the merchant saying
   * stock does not gate selling this. It earns a badge because without one an empty cell
   * meant three different things at once, and "the page has not loaded" was one of them.
   */
  NOT_TRACKED: 'not_tracked'
} as const

type StockLevel = (typeof STOCK_LEVEL)[keyof typeof STOCK_LEVEL]

/**
 * Worst first. A product or a variant takes the worst level among the rows in scope.
 *
 * @remarks
 * The two states that judge nothing sit last on purpose. An unmanaged row must never outrank
 * a real verdict, so it surfaces only when every row in scope is unmanaged — and a variant with
 * no row at all yields to any variant of the same product that has one.
 */
const STOCK_LEVEL_PRIORITY = [
  STOCK_LEVEL.OUT_OF_STOCK,
  STOCK_LEVEL.BELOW_REORDER_POINT,
  STOCK_LEVEL.IN_STOCK,
  STOCK_LEVEL.NO_REORDER_POINT,
  STOCK_LEVEL.NOT_STOCKED,
  STOCK_LEVEL.NOT_TRACKED
] as const

/**
 * "Re-Order Point", spelled as the variant form and the replenishment table spell it.
 * One name for one concept — this badge reports that setting, it does not rename it.
 */
const STOCK_LEVEL_LABELS: LabelMap = {
  [STOCK_LEVEL.OUT_OF_STOCK]: 'Out of Stock',
  [STOCK_LEVEL.BELOW_REORDER_POINT]: 'Below Re-Order Point',
  [STOCK_LEVEL.IN_STOCK]: 'In Stock',
  [STOCK_LEVEL.NO_REORDER_POINT]: 'No Re-Order Point',
  // "Not Stocked", not "No Stock Record": the merchant's own distinction is between a product
  // they do not hold here and one they hold and have run out of, which is "Out of Stock".
  [STOCK_LEVEL.NOT_STOCKED]: 'Not Stocked',
  // "Not Tracked", naming the "Track Quantity" switch on the variant form rather than the
  // column it sits under. The merchant looks for the control they set, not for our word for it.
  [STOCK_LEVEL.NOT_TRACKED]: 'Not Tracked'
  // `satisfies` rather than the type alone: `LabelMap` is a plain `Record<string, string>`, so a
  // seventh state added above would compile with no label and the merchant would read
  // `not_tracked`. This makes that a red typecheck. The declared type stays `LabelMap` because an
  // exact mapped type carries no index signature and `resolveStatusLabel` will not take one.
} satisfies Record<StockLevel, string>

/**
 * Colour is the merchant's cue to act, so only the two states that need action carry it.
 *
 * @remarks
 * `no_reorder_point` is neutral rather than a warning. Most items on a new tenant have no
 * point set, and a screen of amber badges nags about a setting nobody has been asked for
 * yet — the same reasoning that pulled red off every short row on the replenishment screen.
 */
const STOCK_LEVEL_SEVERITY: SeverityMap = {
  [STOCK_LEVEL.OUT_OF_STOCK]: 'danger',
  [STOCK_LEVEL.BELOW_REORDER_POINT]: 'danger',
  [STOCK_LEVEL.IN_STOCK]: 'success',
  [STOCK_LEVEL.NO_REORDER_POINT]: 'contrast',
  // Neutral for the same reason, and more so: three tracking variants in four hold no inventory
  // row on a real tenant today. Amber there would colour most of the screen and mean nothing.
  [STOCK_LEVEL.NOT_STOCKED]: 'contrast',
  // Neutral, and not a warning. Switching tracking off is a decision, not a mistake: on a live
  // tenant 182 of the 242 untracked variants also allow selling out of stock, which is the same
  // decision said twice. Amber would accuse the merchant of their own settings.
  [STOCK_LEVEL.NOT_TRACKED]: 'contrast',
  FALLBACK: 'contrast'
} satisfies Record<StockLevel | 'FALLBACK', StatusSeverity>

export { STOCK_LEVEL, STOCK_LEVEL_LABELS, STOCK_LEVEL_PRIORITY, STOCK_LEVEL_SEVERITY }
export type { StockLevel }
